import { z } from 'zod'

/**
 * DTOs da Parte Diária (MVE / EPP) — espelho das respostas de
 * `GET /parte-diaria/*` e dos payloads de `POST /sync/parte-diaria/*`.
 * Schemas tolerantes (`passthrough`) nas respostas de leitura: campo novo no
 * backend não pode quebrar o app em campo.
 */

const nStr = z.string().nullable()
const nNum = z.number().nullable()

export const TipoParteSchema = z.enum(['M', 'M+C', 'E'])
export const MotivoBloqueioSchema = z.enum(['TRANSFERENCIA', 'MEDICAO_GERADA', 'FORA_CONTRATO', 'OUTRO_CHEFE'])

export const SessaoParteSchema = z
  .object({
    usuarioId: z.number(),
    perfil: z.string(),
    admin: z.boolean(),
    chefeSessaoId: nNum,
    permissoes: z.object({ inserirMve: z.boolean(), inserirEpp: z.boolean(), inibirRetroativo: z.boolean() }),
  })
  .passthrough()

export const MveParteDtoSchema = z
  .object({
    id: z.number(),
    prefixo: nStr,
    marca: nStr,
    modelo: nStr,
    subgrupo: nStr,
    tipo: TipoParteSchema,
    apresentarFicha: z.boolean(),
    mveTp: z.enum(['M', 'V', 'E']),
    primeiroHorimetro: z.number(),
    enviaKmRabbot: z.boolean(),
    chefeEquipeCadastralId: nNum,
    chefeEquipeListaId: nNum,
    transferenciaListaId: nNum,
    transferenciaDtInicial: nStr,
    transferenciaDtFinal: nStr,
    medicaoGerada: z.boolean(),
    qtdMovimentacao: z.number(),
    estado: z.enum(['SEM_LANCAMENTO', 'COM_LANCAMENTO', 'GERADA']).optional(),
  })
  .passthrough()
export type MveParteDto = z.infer<typeof MveParteDtoSchema>

export const MvesParteDiariaResponseSchema = z
  .object({ hoje: z.string(), dt: z.string(), mes: z.string(), sessao: SessaoParteSchema, items: z.array(MveParteDtoSchema) })
  .passthrough()
export type MvesParteDiariaResponse = z.infer<typeof MvesParteDiariaResponseSchema>

export const ContratoMesSchema = z
  .object({
    contratoItemId: z.number(),
    garantia: nNum,
    pup: nNum,
    pui: nNum,
    formularioMedicao: z.enum(['MVE', 'P']),
    unidade: nStr,
    formaRemuneracaoNome: nStr,
    dtInicio: nStr,
    dtFinal: nStr,
  })
  .passthrough()
export type ContratoMesDto = z.infer<typeof ContratoMesSchema>

export const TransferenciaCompletaSchema = z
  .object({
    id: z.number(),
    dt: z.string(),
    destinoId: z.number(),
    origemId: nNum,
    origemDt: nStr,
    origemAnteriorId: nNum,
    origemAnteriorDt: nStr,
  })
  .passthrough()

export const ObraParteDtoSchema = z
  .object({ id: z.number(), codigo: z.string(), nome: nStr, projetoId: z.number(), projetoCmve: z.boolean(), tipoProjetoId: nNum })
  .passthrough()
export type ObraParteDto = z.infer<typeof ObraParteDtoSchema>

export const ServicoParteDtoSchema = z
  .object({
    id: z.number(),
    nome: z.string(),
    isento: z.boolean(),
    grupoId: nNum,
    grupoNome: nStr,
    inativo: z.boolean(),
    coringa: z.boolean(),
    tp: nStr,
  })
  .passthrough()
export type ServicoParteDto = z.infer<typeof ServicoParteDtoSchema>

export const EquipeParteDtoSchema = z
  .object({ id: z.number(), nome: z.string(), encarregadoId: z.number().nullish(), encarregadoNome: z.string().nullish() })
  .passthrough()
export type EquipeParteDto = z.infer<typeof EquipeParteDtoSchema>

const ResumoDiaMveSchema = z.object({
  horimetroSugerido: z.number().nullable(),
  formularioMedicao: z.enum(['MVE', 'P']).nullable(),
  linhasHoje: z.number(),
})

/** GET /parte-diaria/pacote-offline — pré-carga para uso sem sinal (ver parteDiariaOffline.ts). */
export const PacoteOfflineParteDiariaSchema = z
  .object({
    hoje: z.string(),
    dt: z.string(),
    mes: z.string(),
    mves: z.array(MveParteDtoSchema),
    obras: z.array(ObraParteDtoSchema),
    servicos: z.array(ServicoParteDtoSchema),
    universais: z.array(z.number()),
    servicosPorSubgrupo: z.record(z.array(z.number())),
    servicosPorObra: z.record(z.array(z.number())),
    subgrupoPorMve: z.record(z.number().nullable()),
    equipesPorProjeto: z.record(z.array(EquipeParteDtoSchema)),
    /** mveId → horímetro inicial + modo do dia, para TODAS as MVEs de horímetro. */
    resumoDia: z.record(ResumoDiaMveSchema).optional(),
    /** O mesmo resumo calculado para o dia anterior (o app lança ontem e hoje). */
    resumoOntem: z.object({ dt: z.string(), porMve: z.record(ResumoDiaMveSchema) }).optional(),
  })
  .passthrough()
export type PacoteOfflineParteDiariaDto = z.infer<typeof PacoteOfflineParteDiariaSchema>

export const ConfigParteDiariaSchema = z.object({ difKm: nNum, difHora: nNum, horaInicioFim: nNum, diasRetroativo: nNum })

const LinhaMveBaseSchema = z
  .object({
    sigaMveId: z.number(),
    dt: z.string(),
    dia: z.number(),
    obraId: nNum,
    obraCodigo: nStr,
    servicoId: nNum,
    servicoNome: nStr,
    servicoGrupoId: nNum,
    inicio: z.number(),
    termino: z.number(),
    chapa: nStr,
    equipeId: nNum,
    hInicio: nStr,
    hFim: nStr,
    ficha: nStr,
    chefeEquipeId: nNum,
    chefeEquipeNome: nStr,
    transferenciaId: nNum,
    medicaoGerada: z.enum(['S', 'N']),
    observacao: nStr,
    kmReal: z.number(),
    contratoItemId: nNum,
  })
  .passthrough()
export const LinhaMveRowSchema = LinhaMveBaseSchema.extend({ bloqueado: z.boolean(), motivo: MotivoBloqueioSchema.nullable() })
export type LinhaMveRowDto = z.infer<typeof LinhaMveRowSchema>
export type LinhaMveBaseDto = z.infer<typeof LinhaMveBaseSchema>

export const DiaGradeSchema = z.object({
  dt: z.string(),
  dia: z.number(),
  bloqueado: z.boolean(),
  motivo: MotivoBloqueioSchema.nullable(),
  passo: z.number(),
  qtdLinhas: z.number(),
})
export type DiaGradeDto = z.infer<typeof DiaGradeSchema>

const ContextoBaseShape = {
  hoje: z.string(),
  dt: z.string(),
  mes: z.string(),
  diaInicial: z.string(),
  diaFinal: z.string(),
  diasNoMes: z.number(),
  sessao: SessaoParteSchema,
  mve: MveParteDtoSchema,
  contratoMes: ContratoMesSchema.nullable(),
  transferencia: TransferenciaCompletaSchema.nullable(),
  obras: z.array(ObraParteDtoSchema),
  avisos: z.array(z.string()),
  bloqueios: z.array(z.object({ code: z.string(), message: z.string() })),
}

export const ContextoMveSchema = z
  .object({
    ...ContextoBaseShape,
    tipo: z.literal('MVE'),
    configs: ConfigParteDiariaSchema,
    linhasMes: z.array(LinhaMveRowSchema),
    dias: z.array(DiaGradeSchema),
    dia: z
      .object({
        modo: z.enum(['DIA_NOVO', 'FRACOES', 'BLOQUEADO']),
        motivo: MotivoBloqueioSchema.nullable(),
        message: nStr,
        linhasHoje: z.array(LinhaMveBaseSchema),
        horimetroSugerido: nNum,
        contratoDia: ContratoMesSchema.nullable(),
        hodometroTransferenciaNoDia: nNum,
        hodometroTransferenciaDiaSeguinte: nNum,
        ultimoDtChefe: nStr,
      })
      .passthrough(),
  })
  .passthrough()
export type ContextoMveDto = z.infer<typeof ContextoMveSchema>

export const LinhaEppRowSchema = z
  .object({
    sigaEppId: z.number(),
    obraId: z.number(),
    obraCodigo: nStr,
    servicoId: z.number(),
    servicoNome: nStr,
    dtInicial: z.string(),
    dtFinal: z.string(),
    valor: z.number(),
    qr: z.number(),
    chefeEquipeId: nNum,
    chefeEquipeNome: nStr,
    transferenciaId: nNum,
    medicaoGerada: z.enum(['S', 'N']),
    observacao: nStr,
    contratoItemId: nNum,
    isento: z.boolean(),
    pup: nNum,
    bloqueado: z.boolean(),
    motivo: MotivoBloqueioSchema.nullable(),
  })
  .passthrough()
export type LinhaEppRowDto = z.infer<typeof LinhaEppRowSchema>

export const ContextoEppSchema = z
  .object({
    ...ContextoBaseShape,
    tipo: z.literal('EPP'),
    linhasMes: z.array(LinhaEppRowSchema),
    valorRealizadoMes: z.number(),
    podeInserir: z.boolean(),
    motivo: MotivoBloqueioSchema.nullable(),
    message: nStr,
    ultimaDtFinalOutroChefe: nStr,
  })
  .passthrough()
export type ContextoEppDto = z.infer<typeof ContextoEppSchema>

export const ContextoParteDiariaSchema = z.discriminatedUnion('tipo', [ContextoMveSchema, ContextoEppSchema])
export type ContextoParteDiariaDto = z.infer<typeof ContextoParteDiariaSchema>

// ---- escrita ---------------------------------------------------------------

export const LinhaMveDtoSchema = z.object({
  id: z.string(),
  obraId: z.number(),
  servicoExecutadoId: z.number(),
  equipeId: z.number().nullable(),
  ficha: z.string().nullable(),
  medicaoInicio: z.number().nullable(),
  medicaoTermino: z.number(),
  chapa: z.string().nullable(),
  hInicio: z.string(),
  hFim: z.string(),
  observacao: z.string().nullable(),
})
export type LinhaMveDto = z.infer<typeof LinhaMveDtoSchema>

export const LancamentoMveDtoSchema = z.object({
  id: z.string(),
  mveId: z.number(),
  dtMovimentacao: z.string(),
  capturedAt: z.string(),
  modo: z.enum(['DIA_NOVO', 'FRACOES']),
  horimetroInicialEsperado: z.number().nullable(),
  linhas: z.array(LinhaMveDtoSchema).min(1),
})
export type LancamentoMveDto = z.infer<typeof LancamentoMveDtoSchema>

export const LancamentoEppDtoSchema = z.object({
  id: z.string(),
  mveId: z.number(),
  obraId: z.number(),
  servicoExecutadoId: z.number(),
  dtInicial: z.string(),
  dtFinal: z.string(),
  observacao: z.string().nullable(),
  capturedAt: z.string(),
})
export type LancamentoEppDto = z.infer<typeof LancamentoEppDtoSchema>

export const ParteDiariaMveSyncResponseSchema = z
  .object({ ok: z.literal(true), appUuid: z.string(), sigaMveIds: z.array(z.number()), syncedAt: z.string() })
  .passthrough()
export type ParteDiariaMveSyncResponse = z.infer<typeof ParteDiariaMveSyncResponseSchema>

export const ParteDiariaEppSyncResponseSchema = z
  .object({ ok: z.literal(true), appUuid: z.string(), sigaEppId: z.number(), valor: z.number(), valorRealizadoMes: z.number(), syncedAt: z.string() })
  .passthrough()
export type ParteDiariaEppSyncResponse = z.infer<typeof ParteDiariaEppSyncResponseSchema>
